import api from "../utils/api";
import {
  CREATE_PATTERN_REQUEST,
  CREATE_PATTERN_SUCCESS,
  CREATE_PATTERN_FAIL,
  GET_ALL_PATTERNS_REQUEST,
  GET_ALL_PATTERNS_SUCCESS,
  GET_ALL_PATTERNS_FAIL,
  GET_USERS_PATTERNS_FAIL,
  GET_USERS_PATTERNS_SUCCESS,
  GET_USERS_PATTERNS_REQUEST,
  GET_PATTERN_REQUEST,
  GET_PATTERN_SUCCESS,
  GET_PATTERN_FAIL,
  REMOVE_PATTERN_REQUEST,
  REMOVE_PATTERN_SUCCESS,
  REMOVE_PATTERN_FAIL,
  UPDATE_PATTERN_FAIL,
  UPDATE_PATTERN_REQUEST,
  UPDATE_PATTERN_SUCCESS,
  CREATE_PATTERN_REVIEW_REQUEST,
  CREATE_PATTERN_REVIEW_SUCCESS,
  CREATE_PATTERN_REVIEW_FAIL,
} from "./constants/pattern";
import { PatternData } from "../types/siteTypes";

const resolveDisplayCurrency = (_getState: any): "EUR" => "EUR";

type PatternFilters = {
  ucategory?: string | string[];
  client?: string;
  fabrics?: string;
  sizes?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  hasDiscount?: string;
};

export const createPattern = (patternData: PatternData, navigate: any) => async (
  dispatch: any
) => {
  try {
    dispatch({ type: CREATE_PATTERN_REQUEST });
    const res = await api.post("/patterns", patternData);

    dispatch({
      type: CREATE_PATTERN_SUCCESS,
      payload: res.data,
    });

    navigate(`/pattern/${res.data._id}`);
  } catch (err: any) {
    dispatch({
      type: CREATE_PATTERN_FAIL,
      payload:
        err.response && err.response.data.message
          ? err.response.data.message
          : err.message,
    });
  }
};

export const updatePattern = (pattern: PatternData, navigate: any) => async (
  dispatch: any
) => {
  try {
    dispatch({ type: UPDATE_PATTERN_REQUEST });
    const res = await api.put(`/patterns/${pattern._id}`, pattern);

    dispatch({
      type: UPDATE_PATTERN_SUCCESS,
      payload: res.data,
    });
    navigate(`/pattern/${pattern._id}`);
  } catch (err: any) {
    dispatch({
      type: UPDATE_PATTERN_FAIL,
      payload:
        err.response && err.response.data.message
          ? err.response.data.message
          : err.message,
    });
  }
};

// Get current users profile
export const getCurrentUsersPatterns = () => async (dispatch: any, getState: any) => {
  try {
    dispatch({ type: GET_USERS_PATTERNS_REQUEST });
    const currency = resolveDisplayCurrency(getState);
    const query = currency ? `?displayCurrency=${currency}` : "";
    const res = await api.get(`/patterns/myPatterns${query}`);

    dispatch({
      type: GET_USERS_PATTERNS_SUCCESS,
      payload: res.data,
    });
  } catch (err: any) {
    dispatch({
      type: GET_USERS_PATTERNS_FAIL,
      payload:
        err.response && err.response.data.message
          ? err.response.data.message
          : err.message,
    });
  }
};

// Get patterns by user ID
export const getPatternsByUserId = (userId: string) => async (
  dispatch: any,
  getState: any
) => {
  try {
    dispatch({ type: GET_USERS_PATTERNS_REQUEST });
    const params = new URLSearchParams({ user: userId });
    const currency = resolveDisplayCurrency(getState);
    if (currency) {
      params.set("displayCurrency", currency);
    }
    const res = await api.get(`/patterns?${params.toString()}`);

    dispatch({
      type: GET_USERS_PATTERNS_SUCCESS,
      payload: res.data.patterns || res.data,
      pagination: res.data.pagination,
    });
  } catch (err: any) {
    dispatch({
      type: GET_USERS_PATTERNS_FAIL,
      payload:
        err.response && err.response.data.message
          ? err.response.data.message
          : err.message,
    });
  }
};

// Get all patterns with pagination and filters
export const getAllPatterns = (
  keyword = "",
  page = 1,
  limit = 12,
  filters: PatternFilters = {}
) => async (dispatch: any, getState: any) => {
  try {
    dispatch({ type: GET_ALL_PATTERNS_REQUEST });

    const paramsObj: any = {
      keyword,
      page: page,
      limit: limit,
    };
    
    if (filters.ucategory && !filters.ucategory.includes("all") &&
      !filters.ucategory.includes("newArrivals")) {
      paramsObj.categories = filters.ucategory;
    }

    if (filters.client) {
      paramsObj.clients = filters.client;
    }

    if (filters.fabrics) {
      paramsObj.fabrics = filters.fabrics;
    }

    if (filters.sizes) {
      paramsObj.sizes = filters.sizes;
    }

    if (filters.sortBy) {
      paramsObj.sortBy = filters.sortBy;
      paramsObj.sortOrder = filters.sortOrder || 'desc';
    }

    if (filters.hasDiscount) {
      paramsObj.hasDiscount = filters.hasDiscount;
    }

    // Build query string
    const params = new URLSearchParams(paramsObj);
    const currency = resolveDisplayCurrency(getState);
    if (currency) {
      params.set("displayCurrency", currency);
    }

    const res = await api.get(`/patterns?${params.toString()}`);

    dispatch({
      type: GET_ALL_PATTERNS_SUCCESS,
      payload: res.data.patterns || res.data, // Support both old and new format
      pagination: res.data.pagination,
    });
  } catch (err: any) {
    dispatch({
      type: GET_ALL_PATTERNS_FAIL,
      payload:
        err.response && err.response.data.message
          ? err.response.data.message
          : err.message,
    });
  }
};

// Get profile by ID
export const getPatternById = (id: string) => async (
  dispatch: any,
  getState: any
) => {
  try {
    dispatch({ type: GET_PATTERN_REQUEST });
    const params = new URLSearchParams();
    const currency = resolveDisplayCurrency(getState);
    if (currency) {
      params.set("displayCurrency", currency);
    }
    const query = params.toString();
    const res = await api.get(`/patterns/id/${id}${query ? `?${query}` : ""}`);

    dispatch({
      type: GET_PATTERN_SUCCESS,
      payload: res.data,
    });
  } catch (err: any) {
    dispatch({
      type: GET_PATTERN_FAIL,
      payload:
        err.response && err.response.data.message
          ? err.response.data.message
          : err.message,
    });
  }
};

export const removePatternById = (
  id: string,
  navigate: any,
  admin: boolean | undefined = false
) => async (dispatch: any, prevState: any) => {
  try {
    dispatch({
      type: REMOVE_PATTERN_REQUEST,
    });

    await api.delete(`/patterns/${admin ? "admin/" : ""}${id}`);

    if (!admin) {
      navigate("/dashboard");
    }
    dispatch({
      type: REMOVE_PATTERN_SUCCESS,
      payload: id,
    });
  } catch (err: any) {
    dispatch({
      type: REMOVE_PATTERN_FAIL,
      payload:
        err.response && err.response.data.message
          ? err.response.data.message
          : err.message,
    });
  }
};

export const createPatternReview = (
  id: string,
  review: {
    rating: number;
    comment: string;
  }
) => async (dispatch: any) => {
  try {
    dispatch({ type: CREATE_PATTERN_REVIEW_REQUEST });
    await api.post(`/patterns/${id}/reviews`, review);

    dispatch({
      type: CREATE_PATTERN_REVIEW_SUCCESS,
    });
  } catch (err: any) {
    dispatch({
      type: CREATE_PATTERN_REVIEW_FAIL,
      payload:
        err.response && err.response.data.message
          ? err.response.data.message
          : err.message,
    });
  }
};
